import { randomUUID } from "node:crypto";

import { ORGX_WIZARD_STATE_PATH } from "../constants.js";
import { deleteFileIfExists, parseJsonObject, readTextIfExists, writeJsonFile } from "./fs.js";
import { SURFACE_NAMES, type SurfaceName } from "../surfaces/names.js";

export interface WizardContinuityDefaults {
  executionMode?: "cloud" | "local";
  reviewSurface?: SurfaceName;
  workspaceId?: string;
  workspaceName?: string;
}

export interface WizardFirstValueInitiativeRecord {
  createdAt: string;
  id: string;
  liveUrl: string;
  summary?: string;
  title: string;
  workspaceId?: string;
  workspaceName?: string;
}

export interface WizardOnboardingTaskRecord {
  createdAt: string;
  id: string;
  initiativeId?: string;
  status?: string;
  title: string;
  workstreamId?: string;
  workspaceId?: string;
  workspaceName?: string;
}

export interface WizardAgentRosterEntry {
  agentType: string;
  domain: string;
  name: string;
}

export interface WizardAgentRosterRecord {
  agents: WizardAgentRosterEntry[];
  configuredAt: string;
  workspaceId?: string;
  workspaceName?: string;
}

export interface WizardSkillFileRecord {
  contentSha256: string;
  coreSha256?: string;
  path: string;
  skillId: string;
  updatedAt: string;
}

export interface WizardSkillFilesRecord {
  files: WizardSkillFileRecord[];
  updatedAt: string;
}

/**
 * People-first capture idempotency record — keyed by workspace id so the
 * wizard does not re-prompt Wave 3b capture on every run. Mirrors the
 * pattern the Daily Brief uses server-side (`onboardingCompletedAt`). This
 * local record is authoritative while the server endpoints are still
 * landing (Wave 2).
 */
export interface WizardPeopleFirstCaptureEntry {
  workspaceId: string;
  completedAt: string;
  personId?: string;
  templatePersona?: string;
  /** True when the server endpoints were not live at capture time and the
   *  payload was staged to `~/.orgx-wizard/pending-people.json`. */
  stagedLocally?: boolean;
}

export interface WizardPeopleFirstCaptureRecord {
  updatedAt: string;
  entries: WizardPeopleFirstCaptureEntry[];
}

export type WizardDailyBriefOnboardingStatus = "completed" | "skipped";

export interface WizardDailyBriefOnboardingEntry {
  workspaceId: string;
  status: WizardDailyBriefOnboardingStatus;
  completedAt?: string;
  skippedAt?: string;
  workspaceName?: string;
}

export interface WizardDailyBriefOnboardingRecord {
  updatedAt: string;
  entries: WizardDailyBriefOnboardingEntry[];
}

export type WizardSetupPromptKey =
  | "first_initiative"
  | "onboarding_task"
  | "agent_roster"
  | "local_skill_discovery"
  | "setup_intent";

export interface WizardSetupPromptDecisionEntry {
  promptKey: WizardSetupPromptKey;
  skippedAt: string;
  status: "skipped";
  workspaceId: string;
  workspaceName?: string;
}

export interface WizardSetupPromptDecisionsRecord {
  updatedAt: string;
  entries: WizardSetupPromptDecisionEntry[];
}

export interface WizardStateRecord {
  continuity?: WizardContinuityDefaults;
  installationId: string;
  agentRoster?: WizardAgentRosterRecord;
  firstValueInitiative?: WizardFirstValueInitiativeRecord;
  onboardingTask?: WizardOnboardingTaskRecord;
  skillFiles?: WizardSkillFilesRecord;
  peopleFirstCapture?: WizardPeopleFirstCaptureRecord;
  dailyBriefOnboarding?: WizardDailyBriefOnboardingRecord;
  setupPromptDecisions?: WizardSetupPromptDecisionsRecord;
  createdAt: string;
  updatedAt: string;
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function isSurfaceName(value: unknown): value is SurfaceName {
  return typeof value === "string" && SURFACE_NAMES.includes(value as SurfaceName);
}

function parseContinuityDefaults(value: unknown): WizardContinuityDefaults | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return undefined;
  }

  const record = value as Record<string, unknown>;
  const parsed: WizardContinuityDefaults = {};

  if (record.executionMode === "cloud" || record.executionMode === "local") {
    parsed.executionMode = record.executionMode;
  }

  if (isSurfaceName(record.reviewSurface)) {
    parsed.reviewSurface = record.reviewSurface;
  }

  if (isNonEmptyString(record.workspaceId)) {
    parsed.workspaceId = record.workspaceId.trim();
  }

  if (isNonEmptyString(record.workspaceName)) {
    parsed.workspaceName = record.workspaceName.trim();
  }

  return Object.keys(parsed).length > 0 ? parsed : undefined;
}

function parseFirstValueInitiative(
  value: unknown,
): WizardFirstValueInitiativeRecord | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return undefined;
  }

  const record = value as Record<string, unknown>;
  if (
    !isNonEmptyString(record.id) ||
    !isNonEmptyString(record.title) ||
    !isNonEmptyString(record.liveUrl) ||
    !isNonEmptyString(record.createdAt)
  ) {
    return undefined;
  }

  return {
    createdAt: record.createdAt.trim(),
    id: record.id.trim(),
    liveUrl: record.liveUrl.trim(),
    ...(isNonEmptyString(record.summary) ? { summary: record.summary.trim() } : {}),
    title: record.title.trim(),
    ...(isNonEmptyString(record.workspaceId)
      ? { workspaceId: record.workspaceId.trim() }
      : {}),
    ...(isNonEmptyString(record.workspaceName)
      ? { workspaceName: record.workspaceName.trim() }
      : {}),
  };
}

function parseOnboardingTask(value: unknown): WizardOnboardingTaskRecord | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return undefined;
  }

  const record = value as Record<string, unknown>;
  if (
    !isNonEmptyString(record.id) ||
    !isNonEmptyString(record.title) ||
    !isNonEmptyString(record.createdAt)
  ) {
    return undefined;
  }

  return {
    createdAt: record.createdAt.trim(),
    id: record.id.trim(),
    ...(isNonEmptyString(record.initiativeId)
      ? { initiativeId: record.initiativeId.trim() }
      : {}),
    ...(isNonEmptyString(record.status)
      ? { status: record.status.trim() }
      : {}),
    title: record.title.trim(),
    ...(isNonEmptyString(record.workstreamId)
      ? { workstreamId: record.workstreamId.trim() }
      : {}),
    ...(isNonEmptyString(record.workspaceId)
      ? { workspaceId: record.workspaceId.trim() }
      : {}),
    ...(isNonEmptyString(record.workspaceName)
      ? { workspaceName: record.workspaceName.trim() }
      : {}),
  };
}

function parseAgentRosterEntry(value: unknown): WizardAgentRosterEntry | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return undefined;
  }

  const record = value as Record<string, unknown>;
  if (
    !isNonEmptyString(record.agentType) ||
    !isNonEmptyString(record.domain) ||
    !isNonEmptyString(record.name)
  ) {
    return undefined;
  }

  return {
    agentType: record.agentType.trim(),
    domain: record.domain.trim(),
    name: record.name.trim(),
  };
}

function parseAgentRoster(value: unknown): WizardAgentRosterRecord | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return undefined;
  }

  const record = value as Record<string, unknown>;
  if (!isNonEmptyString(record.configuredAt) || !Array.isArray(record.agents)) {
    return undefined;
  }

  const agents = record.agents
    .map((entry) => parseAgentRosterEntry(entry))
    .filter((entry): entry is WizardAgentRosterEntry => Boolean(entry));
  if (agents.length === 0) {
    return undefined;
  }

  return {
    agents,
    configuredAt: record.configuredAt.trim(),
    ...(isNonEmptyString(record.workspaceId)
      ? { workspaceId: record.workspaceId.trim() }
      : {}),
    ...(isNonEmptyString(record.workspaceName)
      ? { workspaceName: record.workspaceName.trim() }
      : {}),
  };
}

function parseSkillFileRecord(value: unknown): WizardSkillFileRecord | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return undefined;
  }

  const record = value as Record<string, unknown>;
  if (
    !isNonEmptyString(record.path) ||
    !isNonEmptyString(record.skillId) ||
    !isNonEmptyString(record.contentSha256) ||
    !isNonEmptyString(record.updatedAt)
  ) {
    return undefined;
  }

  return {
    contentSha256: record.contentSha256.trim(),
    path: record.path.trim(),
    skillId: record.skillId.trim(),
    updatedAt: record.updatedAt.trim(),
    ...(isNonEmptyString(record.coreSha256)
      ? { coreSha256: record.coreSha256.trim() }
      : {}),
  };
}

function parseSkillFiles(value: unknown): WizardSkillFilesRecord | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return undefined;
  }

  const record = value as Record<string, unknown>;
  if (!isNonEmptyString(record.updatedAt) || !Array.isArray(record.files)) {
    return undefined;
  }

  const files = record.files
    .map((entry) => parseSkillFileRecord(entry))
    .filter((entry): entry is WizardSkillFileRecord => Boolean(entry));
  if (files.length === 0) {
    return undefined;
  }

  return {
    files,
    updatedAt: record.updatedAt.trim(),
  };
}

function parsePeopleFirstCaptureEntry(
  value: unknown,
): WizardPeopleFirstCaptureEntry | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return undefined;
  }
  const record = value as Record<string, unknown>;
  if (
    !isNonEmptyString(record.workspaceId) ||
    !isNonEmptyString(record.completedAt)
  ) {
    return undefined;
  }
  return {
    workspaceId: record.workspaceId.trim(),
    completedAt: record.completedAt.trim(),
    ...(isNonEmptyString(record.personId) ? { personId: record.personId.trim() } : {}),
    ...(isNonEmptyString(record.templatePersona)
      ? { templatePersona: record.templatePersona.trim() }
      : {}),
    ...(record.stagedLocally === true ? { stagedLocally: true } : {}),
  };
}

function parsePeopleFirstCapture(
  value: unknown,
): WizardPeopleFirstCaptureRecord | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return undefined;
  }
  const record = value as Record<string, unknown>;
  if (!isNonEmptyString(record.updatedAt) || !Array.isArray(record.entries)) {
    return undefined;
  }
  const entries = record.entries
    .map((entry) => parsePeopleFirstCaptureEntry(entry))
    .filter(
      (entry): entry is WizardPeopleFirstCaptureEntry => Boolean(entry),
    );
  if (entries.length === 0) return undefined;
  return {
    updatedAt: record.updatedAt.trim(),
    entries,
  };
}

function parseDailyBriefOnboardingEntry(
  value: unknown,
): WizardDailyBriefOnboardingEntry | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return undefined;
  }

  const record = value as Record<string, unknown>;
  const status = record.status;
  if (
    !isNonEmptyString(record.workspaceId) ||
    (status !== "completed" && status !== "skipped")
  ) {
    return undefined;
  }

  const completedAt = isNonEmptyString(record.completedAt)
    ? record.completedAt.trim()
    : undefined;
  const skippedAt = isNonEmptyString(record.skippedAt)
    ? record.skippedAt.trim()
    : undefined;

  if (status === "completed" && !completedAt) return undefined;
  if (status === "skipped" && !skippedAt) return undefined;

  return {
    workspaceId: record.workspaceId.trim(),
    status,
    ...(completedAt ? { completedAt } : {}),
    ...(skippedAt ? { skippedAt } : {}),
    ...(isNonEmptyString(record.workspaceName)
      ? { workspaceName: record.workspaceName.trim() }
      : {}),
  };
}

function parseDailyBriefOnboarding(
  value: unknown,
): WizardDailyBriefOnboardingRecord | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return undefined;
  }

  const record = value as Record<string, unknown>;
  if (!isNonEmptyString(record.updatedAt) || !Array.isArray(record.entries)) {
    return undefined;
  }

  const entries = record.entries
    .map((entry) => parseDailyBriefOnboardingEntry(entry))
    .filter(
      (entry): entry is WizardDailyBriefOnboardingEntry => Boolean(entry),
    );
  if (entries.length === 0) return undefined;

  return {
    updatedAt: record.updatedAt.trim(),
    entries,
  };
}

function isSetupPromptKey(value: unknown): value is WizardSetupPromptKey {
  return (
    value === "first_initiative" ||
    value === "onboarding_task" ||
    value === "agent_roster" ||
    value === "local_skill_discovery" ||
    value === "setup_intent"
  );
}

function parseSetupPromptDecisionEntry(
  value: unknown,
): WizardSetupPromptDecisionEntry | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return undefined;
  }

  const record = value as Record<string, unknown>;
  if (
    !isNonEmptyString(record.workspaceId) ||
    !isSetupPromptKey(record.promptKey) ||
    record.status !== "skipped" ||
    !isNonEmptyString(record.skippedAt)
  ) {
    return undefined;
  }

  return {
    promptKey: record.promptKey,
    skippedAt: record.skippedAt.trim(),
    status: "skipped",
    workspaceId: record.workspaceId.trim(),
    ...(isNonEmptyString(record.workspaceName)
      ? { workspaceName: record.workspaceName.trim() }
      : {}),
  };
}

function parseSetupPromptDecisions(
  value: unknown,
): WizardSetupPromptDecisionsRecord | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return undefined;
  }

  const record = value as Record<string, unknown>;
  if (!isNonEmptyString(record.updatedAt) || !Array.isArray(record.entries)) {
    return undefined;
  }

  const entries = record.entries
    .map((entry) => parseSetupPromptDecisionEntry(entry))
    .filter(
      (entry): entry is WizardSetupPromptDecisionEntry => Boolean(entry),
    );
  if (entries.length === 0) return undefined;

  return {
    updatedAt: record.updatedAt.trim(),
    entries,
  };
}

function createWizardState(now = new Date().toISOString()): WizardStateRecord {
  return {
    installationId: `wizard-${randomUUID()}`,
    createdAt: now,
    updatedAt: now,
  };
}

function sanitizeWizardStateRecord(record: WizardStateRecord): WizardStateRecord {
  const continuity = parseContinuityDefaults(record.continuity);
  const agentRoster = parseAgentRoster(record.agentRoster);
  const firstValueInitiative = parseFirstValueInitiative(record.firstValueInitiative);
  const onboardingTask = parseOnboardingTask(record.onboardingTask);
  const skillFiles = parseSkillFiles(record.skillFiles);
  const peopleFirstCapture = parsePeopleFirstCapture(record.peopleFirstCapture);
  const dailyBriefOnboarding = parseDailyBriefOnboarding(record.dailyBriefOnboarding);
  const setupPromptDecisions = parseSetupPromptDecisions(record.setupPromptDecisions);

  return {
    installationId: record.installationId.trim(),
    createdAt: record.createdAt.trim(),
    updatedAt: record.updatedAt.trim(),
    ...(continuity ? { continuity } : {}),
    ...(agentRoster ? { agentRoster } : {}),
    ...(firstValueInitiative ? { firstValueInitiative } : {}),
    ...(onboardingTask ? { onboardingTask } : {}),
    ...(skillFiles ? { skillFiles } : {}),
    ...(peopleFirstCapture ? { peopleFirstCapture } : {}),
    ...(dailyBriefOnboarding ? { dailyBriefOnboarding } : {}),
    ...(setupPromptDecisions ? { setupPromptDecisions } : {}),
  };
}

export function readWizardState(
  statePath: string = ORGX_WIZARD_STATE_PATH,
): WizardStateRecord | null {
  const parsed = parseJsonObject(readTextIfExists(statePath));

  if (
    !isNonEmptyString(parsed.installationId) ||
    !isNonEmptyString(parsed.createdAt) ||
    !isNonEmptyString(parsed.updatedAt)
  ) {
    return null;
  }

  const state: WizardStateRecord = {
    installationId: parsed.installationId.trim(),
    createdAt: parsed.createdAt.trim(),
    updatedAt: parsed.updatedAt.trim(),
  };
  const continuity = parseContinuityDefaults(parsed.continuity);
  if (continuity !== undefined) state.continuity = continuity;
  const agentRoster = parseAgentRoster(parsed.agentRoster);
  if (agentRoster !== undefined) state.agentRoster = agentRoster;
  const firstValueInitiative = parseFirstValueInitiative(parsed.firstValueInitiative);
  if (firstValueInitiative !== undefined) {
    state.firstValueInitiative = firstValueInitiative;
  }
  const onboardingTask = parseOnboardingTask(parsed.onboardingTask);
  if (onboardingTask !== undefined) state.onboardingTask = onboardingTask;
  const skillFiles = parseSkillFiles(parsed.skillFiles);
  if (skillFiles !== undefined) state.skillFiles = skillFiles;
  const peopleFirstCapture = parsePeopleFirstCapture(parsed.peopleFirstCapture);
  if (peopleFirstCapture !== undefined) state.peopleFirstCapture = peopleFirstCapture;
  const dailyBriefOnboarding = parseDailyBriefOnboarding(parsed.dailyBriefOnboarding);
  if (dailyBriefOnboarding !== undefined) {
    state.dailyBriefOnboarding = dailyBriefOnboarding;
  }
  const setupPromptDecisions = parseSetupPromptDecisions(parsed.setupPromptDecisions);
  if (setupPromptDecisions !== undefined) {
    state.setupPromptDecisions = setupPromptDecisions;
  }
  return state;
}

/**
 * Idempotency helpers for people-first capture. `hasCompleted` is called
 * before prompting; `recordCompletion` is called after a successful capture
 * (server-backed or local-fallback) so subsequent wizard runs skip.
 */
export function hasPeopleFirstCaptureCompleted(
  workspaceId: string,
  statePath: string = ORGX_WIZARD_STATE_PATH,
): boolean {
  const state = readWizardState(statePath);
  if (!state?.peopleFirstCapture) return false;
  return state.peopleFirstCapture.entries.some(
    (entry) => entry.workspaceId === workspaceId,
  );
}

export function recordPeopleFirstCaptureCompletion(
  entry: WizardPeopleFirstCaptureEntry,
  statePath: string = ORGX_WIZARD_STATE_PATH,
): WizardStateRecord {
  return updateWizardState((current) => {
    const existingEntries = current.peopleFirstCapture?.entries ?? [];
    const withoutExisting = existingEntries.filter(
      (e) => e.workspaceId !== entry.workspaceId,
    );
    return {
      ...current,
      peopleFirstCapture: {
        updatedAt: new Date().toISOString(),
        entries: [...withoutExisting, entry],
      },
    };
  }, statePath);
}

export function getDailyBriefOnboardingDecision(
  workspaceId: string,
  statePath: string = ORGX_WIZARD_STATE_PATH,
): WizardDailyBriefOnboardingEntry | null {
  const state = readWizardState(statePath);
  return state?.dailyBriefOnboarding?.entries.find(
    (entry) => entry.workspaceId === workspaceId,
  ) ?? null;
}

export function hasDailyBriefOnboardingDecision(
  workspaceId: string,
  statePath: string = ORGX_WIZARD_STATE_PATH,
): boolean {
  return getDailyBriefOnboardingDecision(workspaceId, statePath) !== null;
}

export function recordDailyBriefOnboardingDecision(
  entry: WizardDailyBriefOnboardingEntry,
  statePath: string = ORGX_WIZARD_STATE_PATH,
): WizardStateRecord {
  return updateWizardState((current) => {
    const existingEntries = current.dailyBriefOnboarding?.entries ?? [];
    const withoutExisting = existingEntries.filter(
      (e) => e.workspaceId !== entry.workspaceId,
    );
    return {
      ...current,
      dailyBriefOnboarding: {
        updatedAt: new Date().toISOString(),
        entries: [...withoutExisting, entry],
      },
    };
  }, statePath);
}

export function getSetupPromptDecision(
  workspaceId: string,
  promptKey: WizardSetupPromptKey,
  statePath: string = ORGX_WIZARD_STATE_PATH,
): WizardSetupPromptDecisionEntry | null {
  const state = readWizardState(statePath);
  return (
    state?.setupPromptDecisions?.entries.find(
      (entry) =>
        entry.workspaceId === workspaceId && entry.promptKey === promptKey,
    ) ?? null
  );
}

export function hasSetupPromptSkip(
  workspaceId: string,
  promptKey: WizardSetupPromptKey,
  statePath: string = ORGX_WIZARD_STATE_PATH,
): boolean {
  return getSetupPromptDecision(workspaceId, promptKey, statePath) !== null;
}

export function recordSetupPromptSkip(
  entry: {
    promptKey: WizardSetupPromptKey;
    skippedAt?: string;
    workspaceId: string;
    workspaceName?: string;
  },
  statePath: string = ORGX_WIZARD_STATE_PATH,
): WizardStateRecord {
  return updateWizardState((current) => {
    const existingEntries = current.setupPromptDecisions?.entries ?? [];
    const withoutExisting = existingEntries.filter(
      (existing) =>
        !(
          existing.workspaceId === entry.workspaceId &&
          existing.promptKey === entry.promptKey
        ),
    );
    return {
      ...current,
      setupPromptDecisions: {
        updatedAt: new Date().toISOString(),
        entries: [
          ...withoutExisting,
          {
            promptKey: entry.promptKey,
            skippedAt: entry.skippedAt ?? new Date().toISOString(),
            status: "skipped",
            workspaceId: entry.workspaceId,
            ...(entry.workspaceName ? { workspaceName: entry.workspaceName } : {}),
          },
        ],
      },
    };
  }, statePath);
}

export function writeWizardState(
  value: WizardStateRecord,
  statePath: string = ORGX_WIZARD_STATE_PATH,
): WizardStateRecord {
  const record = sanitizeWizardStateRecord(value);
  writeJsonFile(statePath, record, { mode: 0o600 });
  return record;
}

export function updateWizardState(
  updater: (current: WizardStateRecord) => WizardStateRecord,
  statePath: string = ORGX_WIZARD_STATE_PATH,
): WizardStateRecord {
  const current = readWizardState(statePath) ?? createWizardState();
  const next = updater(current);
  const sanitized = sanitizeWizardStateRecord({
    ...next,
    installationId: next.installationId || current.installationId,
    createdAt: next.createdAt || current.createdAt,
    updatedAt: new Date().toISOString(),
  });
  writeJsonFile(statePath, sanitized, { mode: 0o600 });
  return sanitized;
}

export function getOrCreateWizardInstallationId(
  statePath: string = ORGX_WIZARD_STATE_PATH,
): string {
  const existing = readWizardState(statePath);
  if (existing) {
    return existing.installationId;
  }

  const record = createWizardState();
  writeWizardState(record, statePath);
  return record.installationId;
}

export function clearWizardState(
  statePath: string = ORGX_WIZARD_STATE_PATH,
): boolean {
  return deleteFileIfExists(statePath);
}
